import {
  ChallengeDifficultyBadge,
  ChallengeTypeBadge,
  MaxCompletionsBadge,
  SpeedBonusBadge,
  type ChallengeType,
  type ChallengeDifficulty,
} from '@/components/challenges';
import type { TemplateChallengeGroup, TemplateChallengeLink } from '@/config/objects/templates';
import { cn } from '@/lib/utils';
import { getTeamIcon } from '@/utils/teamIcons';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowRight,
  Flag,
  FolderOutput,
  GripVertical,
  Layers,
  Lightbulb,
  MoreHorizontal,
  Pencil,
  Plus,
  Settings,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface TemplateGroupCardProps {
  templateId: string;
  group: TemplateChallengeGroup;
  allGroups: TemplateChallengeGroup[];
  allChallenges: TemplateChallengeLink[];
  onEdit: () => void;
  onEditUnlock: () => void;
  onAddChallenge: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const COLOR_CLASSES: Record<string, { bg: string; border: string; text: string }> = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  pink: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700' },
};

function getUnlockDescription(group: TemplateChallengeGroup): { icon: React.ElementType; text: string } {
  const condition = group.unlockConditions[0];
  if (!condition) {
    return { icon: Flag, text: 'No unlock condition set' };
  }

  switch (condition.conditionType) {
    case 'starter':
      return { icon: Flag, text: 'Available from start' };
    case 'after_challenge':
      return {
        icon: Lightbulb,
        text: `After "${condition.triggerChallenge?.libraryChallenge?.name || 'Unknown'}"`,
      };
    case 'after_group':
      return {
        icon: Layers,
        text: `After "${condition.triggerGroup?.name || 'Unknown'}" group`,
      };
    default:
      return { icon: Flag, text: 'Unknown condition' };
  }
}

export function TemplateGroupCard({
  templateId,
  group,
  allGroups,
  onEdit,
  onEditUnlock,
  onAddChallenge,
}: TemplateGroupCardProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TemplateChallengeLink | null>(null);

  const colorClasses = COLOR_CLASSES[group.color || 'blue'] || COLOR_CLASSES.blue;
  const GroupIcon = group.icon ? getTeamIcon(group.icon) : null;
  const unlockInfo = getUnlockDescription(group);
  const UnlockIcon = unlockInfo.icon;

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return apiClient.delete(`/templates/${templateId}/groups/${group.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      toast.success('Group deleted');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to delete group');
    },
  });

  const removeChallengeMutation = useMutation({
    mutationFn: async (linkId: string) => {
      return apiClient.delete(`/templates/${templateId}/challenges/${linkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      queryClient.invalidateQueries({ queryKey: ['TemplateChallenges', templateId] });
      toast.success('Challenge removed');
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to remove challenge');
    },
  });

  const moveToGroupMutation = useMutation({
    mutationFn: async ({ linkId, groupId }: { linkId: string; groupId: string | null }) => {
      return apiClient.patch(`/templates/${templateId}/challenges/${linkId}`, {
        groupId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      queryClient.invalidateQueries({ queryKey: ['TemplateChallenges', templateId] });
      toast.success('Challenge moved');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to move challenge');
    },
  });

  const handleMoveToGroup = (linkId: string, targetGroupId: string | null) => {
    moveToGroupMutation.mutate({ linkId, groupId: targetGroupId });
  };

  // Other groups for move menu (excluding current group)
  const otherGroups = allGroups.filter((g) => g.id !== group.id);

  const handleDeleteGroup = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Card className={cn('rounded-xl border shadow-sm !py-0 !gap-0', colorClasses.border)}>
        <CardHeader className={cn('px-4 py-3 border-b flex flex-row items-center justify-between rounded-t-xl', colorClasses.bg, colorClasses.border)}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {GroupIcon && <GroupIcon className={cn('h-4 w-4 shrink-0', colorClasses.text)} />}
                <h3 className={cn('font-semibold truncate', colorClasses.text)}>
                  {group.name}
                </h3>
                <span className="text-xs bg-white/80 px-2 py-0.5 rounded-full text-muted-foreground shrink-0">
                  {group.unlockMode === 'sequential' ? 'Sequential' : 'All at once'}
                </span>
              </div>
              <button
                onClick={onEditUnlock}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-0.5"
              >
                <UnlockIcon className="h-3 w-3" />
                <span>{unlockInfo.text}</span>
                <Pencil className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100" />
              </button>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Group
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEditUnlock}>
                <Settings className="h-4 w-4 mr-2" />
                Set Unlock Condition
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteConfirm(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Group
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>

        <CardContent className="p-0">
          {group.challenges.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No challenges in this group yet.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={onAddChallenge}
                className="mt-3"
                icon={Plus}
              >
                Add Challenge
              </Button>
            </div>
          ) : (
            <ul className="divide-y">
              {group.challenges.map((link, index) => (
                <li
                  key={link.id}
                  className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-center gap-2 text-muted-foreground shrink-0">
                    <GripVertical className="h-4 w-4 opacity-0 group-hover:opacity-100" />
                    {group.unlockMode === 'sequential' ? (
                      <span className="text-sm font-medium w-5 text-center">{index + 1}</span>
                    ) : (
                      <span className="w-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/challenges/${link.libraryChallenge.id}`}
                        className="font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {link.libraryChallenge.name}
                      </Link>
                      {link.maxCompletions != null && (
                        <MaxCompletionsBadge maxCompletions={link.maxCompletions} />
                      )}
                      <SpeedBonusBadge speedBonuses={link.speedBonuses} />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <ChallengeTypeBadge type={link.libraryChallenge.type as ChallengeType} />
                      <ChallengeDifficultyBadge difficulty={link.libraryChallenge.difficulty as ChallengeDifficulty} />
                      <span className="text-xs text-muted-foreground">
                        {link.libraryChallenge.points} pts
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/templates/${templateId}/challenges/${link.id}/settings`)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Challenge Settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          Move to...
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {otherGroups.map((targetGroup) => (
                            <DropdownMenuItem
                              key={targetGroup.id}
                              onClick={() => handleMoveToGroup(link.id, targetGroup.id)}
                            >
                              <Layers className="h-4 w-4 mr-2" />
                              {targetGroup.name}
                            </DropdownMenuItem>
                          ))}
                          {otherGroups.length > 0 && <DropdownMenuSeparator />}
                          <DropdownMenuItem
                            onClick={() => handleMoveToGroup(link.id, null)}
                          >
                            <FolderOutput className="h-4 w-4 mr-2" />
                            Ungrouped
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteTarget(link)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove from Template
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          )}

          {group.challenges.length > 0 && (
            <div className="p-3 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={onAddChallenge}
                className="w-full justify-center text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Challenge
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connector arrow to next group */}
      <div className="flex justify-center py-2">
        <ArrowDown className="h-5 w-5 text-muted-foreground/50" />
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Group"
        description={`Are you sure you want to delete "${group.name}"? Challenges in this group will be unassigned but not deleted.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteGroup}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remove Challenge"
        description={`Are you sure you want to remove "${deleteTarget?.libraryChallenge.name}" from this template?`}
        confirmText="Remove"
        variant="destructive"
        loading={removeChallengeMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) removeChallengeMutation.mutate(deleteTarget.id);
        }}
      />

    </>
  );
}
